import * as React from 'react';
import { Download } from 'lucide-react';

import { Button } from '../primitives/button';
import { toast } from '../primitives/sonner';

/**
 * One section of a multi-section evidence bundle. The bundle is the
 * thing operators / auditors hand to regulators — a single .json
 * archive with a manifest line per section + the raw payload.
 *
 * Example bundles:
 *   - PLM release package: documents + ECO + approvals + signatures
 *   - EBR record:          batch record + sections + deviations + signatures + audit chain
 *   - Quality CAPA:        NCR + RCA + corrective + preventive + effectiveness
 *   - Finance close:       trial balance + adjustments + signoffs
 *   - Compliance validation: IQ/OQ/PQ runs + signoffs + evidence files
 *   - Sustainability assurance: factor library + consumptions + verdict
 *   - Alarm shift review:  active alarms + acknowledgements + supervisor signoff
 */
export interface EvidenceSection {
  /** Stable id — becomes the section key in the bundle JSON. */
  id: string;
  /** Human-readable label rendered in the file's manifest. */
  label: string;
  /**
   * Section payload. Anything JSON-serialisable — entity rows, audit
   * log, hash chain, signed envelope, etc.
   */
  payload: unknown;
  /** Optional content-hash (SHA-256 hex). Computed if absent. */
  hash?: string;
}

export interface EvidenceManifest {
  /** Stable id of the underlying record (work order id, NCR id, etc). */
  recordId: string;
  /** Display label used in the file name and manifest header. */
  recordLabel: string;
  /** Bundle kind — drives the file-name prefix. */
  kind:
    | 'plm-release'
    | 'ebr-record'
    | 'quality-capa'
    | 'finance-close'
    | 'compliance-validation'
    | 'sustainability-assurance'
    | 'alarm-shift-review'
    | string;
  /** Optional generation timestamp (ISO). Defaults to now. */
  generatedAt?: string;
  /** Optional tenant / site / actor stamped onto the manifest. */
  tenantId?: string;
  generatedBy?: string;
}

/**
 * Compute a SHA-256 hex string of an arbitrary JSON-serializable value.
 * Used to stamp each section so an auditor can detect tampering.
 */
export async function hashJson(value: unknown): Promise<string> {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build the canonical bundle envelope from a list of sections.
 * Returns the envelope + a derived filename suggestion.
 */
export async function buildEvidenceBundle(
  manifest: EvidenceManifest,
  sections: EvidenceSection[],
): Promise<{ envelope: unknown; fileName: string }> {
  const generatedAt = manifest.generatedAt ?? new Date().toISOString();
  const hashedSections = await Promise.all(
    sections.map(async (s) => ({
      id: s.id,
      label: s.label,
      hash: s.hash ?? (await hashJson(s.payload)),
      payload: s.payload,
    })),
  );

  const envelope = {
    schema: 'tyndo.evidence/v1',
    manifest: {
      recordId: manifest.recordId,
      recordLabel: manifest.recordLabel,
      kind: manifest.kind,
      generatedAt,
      tenantId: manifest.tenantId,
      generatedBy: manifest.generatedBy,
      sectionIndex: hashedSections.map((s) => ({ id: s.id, label: s.label, hash: s.hash })),
    },
    sections: Object.fromEntries(hashedSections.map((s) => [s.id, s])),
  };

  const safeRecord = manifest.recordLabel.replace(/[^a-zA-Z0-9-]+/g, '-').slice(0, 64);
  const fileName = `${manifest.kind}-${safeRecord}-${generatedAt.slice(0, 10)}.json`;
  return { envelope, fileName };
}

/**
 * Trigger a browser download for the given evidence envelope.
 * Pure client-side — no server roundtrip required when the sections
 * are already in hand (e.g. fetched via React Query during the view).
 */
export function downloadEvidenceBundle(envelope: unknown, fileName: string) {
  const blob = new Blob([JSON.stringify(envelope, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay so older browsers finish reading the stream.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface EvidenceExportButtonProps {
  manifest: EvidenceManifest;
  /**
   * Section resolver. Called when the user clicks the button. Returning
   * an async value lets the consumer lazy-fetch dependent payloads
   * (signatures, audit links, etc.) just-in-time.
   */
  sections: EvidenceSection[] | (() => Promise<EvidenceSection[]>);
  /** Optional button label override. */
  label?: string;
  /** Optional variant. */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  /** Disable when the source data is still loading. */
  disabled?: boolean;
  /**
   * Optional failure handler. Called when section resolution / bundle build /
   * download throws. When omitted, the failure is surfaced via a toast so a
   * failed export is never silent. Provide this to route the error into a
   * view's own error state / logging instead.
   */
  onError?: (error: unknown) => void;
}

/**
 * Drop-in button that builds + downloads an evidence bundle when clicked.
 *
 * @example
 * <EvidenceExportButton
 *   manifest={{ recordId: r.id, recordLabel: r.batchNumber, kind: 'ebr-record' }}
 *   sections={async () => [
 *     { id: 'record', label: 'Batch record', payload: r },
 *     { id: 'sections', label: 'Sections', payload: sectionsQuery.data },
 *     { id: 'signatures', label: 'Signatures', payload: sigsQuery.data },
 *     { id: 'audit-chain', label: 'Audit chain', payload: chainQuery.data },
 *   ]}
 * />
 */
export function EvidenceExportButton({
  manifest,
  sections,
  label = 'Export evidence',
  variant = 'outline',
  disabled = false,
  onError,
}: EvidenceExportButtonProps) {
  const [pending, setPending] = React.useState(false);

  return (
    <Button
      variant={variant}
      size="sm"
      disabled={pending || disabled}
      onClick={async () => {
        setPending(true);
        try {
          const resolved = typeof sections === 'function' ? await sections() : sections;
          const { envelope, fileName } = await buildEvidenceBundle(manifest, resolved);
          downloadEvidenceBundle(envelope, fileName);
        } catch (error) {
          // A failed bundle must not silently reset with no feedback. Surface it
          // (default: toast; consumers can override via onError) so the operator
          // knows the evidence archive was NOT produced.
          if (onError) {
            onError(error);
          } else {
            const detail = error instanceof Error ? error.message : String(error);
            toast.error(`Could not build ${manifest.recordLabel} evidence bundle`, {
              description: detail,
            });
          }
        } finally {
          setPending(false);
        }
      }}
      aria-label={label}
    >
      <Download className="h-4 w-4 mr-1" aria-hidden="true" />
      {pending ? 'Building…' : label}
    </Button>
  );
}

EvidenceExportButton.displayName = 'EvidenceExportButton';
